import React, { useEffect, useMemo, useState, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  RadioGroup,
  Radio,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip,
  IconButton,
  useMediaQuery
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { de } from 'date-fns/locale';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  FlashOn as ImmediateIcon,
  Bookmarks as TemplateIcon,
  Bookmark as BookmarkIcon,
  Repeat as RecurringIcon,
  Timer as TimerIcon,
  DirectionsCar as CarIcon,
  AirportShuttle as KombiIcon,
  ElectricRickshaw as MinibusIcon,
  SwapVert as SwapIcon,
  LocalHospital as MedicalIcon
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { createGlobalStyle } from 'styled-components';
import { APIProvider } from '@vis.gl/react-google-maps';
import PlaceAutocomplete from '../common/PlaceAutocomplete';

import { 
  fetchRideById, 
  createRide, 
  assignRide, 
  updateRideStatus, 
  updateRide,
  fetchRideTemplates,
  saveRideTemplate
} from '../../redux/slices/ridesSlice';
import { fetchDrivers } from '../../redux/slices/driversSlice';
import { fetchVehicles } from '../../redux/slices/vehiclesSlice';
import { fetchCustomers, createCustomer } from '../../redux/slices/customersSlice';
import { fetchMedicalTransportOrders } from '../../redux/slices/medicalTransportOrdersSlice';
import { VEHICLE_TYPE_OPTIONS, CAPABILITIES_PER_TYPE, SPECIAL_CAPABILITIES, getVehicleTypeDescription, migrateSpecialVehicleType } from '../../config/vehicleTypes';
import { APPROVAL_STATUS_OPTIONS, formatDate, getOption } from '../medicalTransportOrders/medicalTransportOrderOptions';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const DEFAULT_PHONE_CUSTOMER_NAME = 'Laufkunde';

const toDateOrNull = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toIsoOrNull = (value) => {
  const date = toDateOrNull(value);
  return date ? date.toISOString() : null;
};

const startOfDay = (value) => {
  const date = toDateOrNull(value);
  if (!date) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const endOfDay = (value) => {
  const date = toDateOrNull(value);
  if (!date) return null;
  date.setHours(23, 59, 59, 999);
  return date;
};

const countRecurringDates = (startValue, endValue, weekdays = []) => {
  const start = startOfDay(startValue);
  const end = endOfDay(endValue);
  if (!start || !end || !Array.isArray(weekdays) || weekdays.length === 0 || end < start) return 0;

  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    if (weekdays.includes(cursor.getDay())) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

const buildMedicalTransportSnapshot = (order) => {
  if (!order) return null;
  return {
    id: order.id,
    patientName: order.patientName || '',
    patientPhone: order.patientPhone || '',
    healthInsuranceName: order.healthInsuranceName || '',
    insuranceNumber: order.insuranceNumber || '',
    prescriptionNumber: order.prescriptionNumber || '',
    approvalNumber: order.approvalNumber || '',
    approvalStatus: order.approvalStatus || 'missing',
    validFrom: toIsoOrNull(order.validFrom),
    validUntil: toIsoOrNull(order.validUntil),
    maxRides: Number(order.maxRides) || 0,
    usedRides: Number(order.usedRides) || 0,
    plannedRides: Number(order.plannedRides) || 0,
    routeScope: order.routeScope || 'fixed_route',
    treatmentType: order.treatmentType || 'other',
    pickupAddress: order.pickupAddress || '',
    destinationAddress: order.destinationAddress || '',
    defaultVehicleType: Number(order.defaultVehicleType) || 1,
    requiredCapabilities: Array.isArray(order.requiredCapabilities) ? order.requiredCapabilities : [],
    documentStatus: order.documentStatus || 'incomplete',
    billingStatus: order.billingStatus || 'not_ready'
  };
};


// Globale Styles für Google Maps Autocomplete
const GlobalStyle = createGlobalStyle`
  .pac-container {
    z-index: 1500 !important;
    border-radius: 4px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    position: fixed !important; /* Wichtig: fixed positioning für bessere Scroll-Behandlung */
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid rgba(0, 0, 0, 0.12);
    background: white;
    
    
    /* Mobile Anpassungen */
    @media (max-width: 768px) {
      max-height: 150px;
      font-size: 14px;
    }
    
    .pac-item {
      padding: 12px 16px;
      cursor: pointer;
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
      transition: background-color 0.2s ease;
      line-height: 1.4;
      
      
      &:last-child {
        border-bottom: none;
      }
      
      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }
      
      &.pac-item-selected {
        background-color: rgba(25, 118, 210, 0.08);
      }
      
      /* Mobile Anpassungen */
      @media (max-width: 768px) {
        padding: 10px 12px;
        min-height: 44px; /* Mindesthöhe für Touch-Geräte */
      }
    }
    
    .pac-icon {
      display: none !important;
    }
    
    .pac-item-query {
      font-size: 14px;
      color: rgba(0, 0, 0, 0.87);
      
      
      @media (max-width: 768px) {
        font-size: 13px;
      }
    }
    
    .pac-matched {
      font-weight: 600;
      color: #1976d2;
    }
    
    .pac-item-context {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.6);
      margin-top: 2px;
      
      @media (max-width: 768px) {
        font-size: 11px;
      }
    }
  }
  
  /* Verhindern, dass das Dropdown bei Touch-Geräten zu schnell verschwindet */
  @media (max-width: 768px) {
    .pac-container {
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }
  }
`;



const OrderTypeBox = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
}));

const RideForm = () => {
  
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isAssignMode = !!id && location.pathname.endsWith('/zuweisen');
  const isEditMode = !!id && !isAssignMode;
  
  const { currentRide, templates, isLoading: rideLoading, error: rideError } = useSelector(state => state.rides);
  const { drivers, isLoading: driversLoading } = useSelector(state => state.drivers);
  const { vehicles, isLoading: vehiclesLoading } = useSelector(state => state.vehicles);
  const { customers, isLoading: customersLoading } = useSelector(state => state.customers);
  const {
    orders: medicalTransportOrders,
    isLoading: medicalTransportOrdersLoading
  } = useSelector(state => state.medicalTransportOrders);

  // Echtzeit-Wartezeiten aus Firebase
  const [waitTimes, setWaitTimes] = useState(null);
  const waitTimeUnsubRef = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'waitTimes', 'current'), (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setWaitTimes({
          general: d.general || '–',
          grossraum: d.grossraum || '–',
          kleinbus7: d.kleinbus7 || '–',
          kleinbus8: d.kleinbus8 || '–',
        });
      }
    });
    waitTimeUnsubRef.current = unsub;
    return () => unsub();
  }, []);

  // Ermittle welche Fahrzeugtypen tatsächlich existieren
  const existingVehicleTypes = useMemo(() => {
    if (!vehicles || vehicles.length === 0) return new Set([1]);
    const types = new Set();
    vehicles.forEach(v => {
      const vType = Number(v.vehicleType || v.vehicle_type || 1);
      types.add(vType);
    });
    return types;
  }, [vehicles]);

  const [formData, setFormData] = useState({
    // Ride Information
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    pickup_address: '',
    pickup_location: { latitude: null, longitude: null },
    destination_address: '',
    destination_location: { latitude: null, longitude: null },
    order_type: '2', // 2: Sofortbestellung, 1: Vorbestellung, 3: Dauerauftrag
    scheduled_time: null,
    vehicle_type: '1', // 1: PKW, 2: Großraum, 3: Kleinbus 7, 4: Kleinbus 8
    special_capabilities: [], // Spezialfähigkeiten als Array
    notes: '', // Notizen für den Fahrer
    is_krankenfahrt: false, // Krankenfahrt
    medical_transport_order_id: '',
    is_rechnungsfahrt: false, // Rechnungsfahrt
    is_festpreis: false,
    festpreis_eur: '',

    // Recurring Order Information (for order_type '3')
    recurring_start_date: null,
    recurring_end_date: null,
    recurring_time: null,
    recurring_weekdays: [], // Array of weekday numbers (0=Sunday, 1=Monday, etc.)
    
    // Assignment Information (for assign mode or direct assignment)
    driver_id: '',
    vehicle_id: ''
  });
  
  const [createNewCustomer, setCreateNewCustomer] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  const [showNoDestinationDialog, setShowNoDestinationDialog] = useState(false);
  
  const [formErrors, setFormErrors] = useState({
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    pickup_address: '',
    destination_address: '',
    scheduled_time: '',
    recurring_start_date: '',
    recurring_end_date: '',
    recurring_time: '',
    recurring_weekdays: '',
    medical_transport_order_id: '',
    driver_id: '',
    vehicle_id: ''
  });
  
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  
  useEffect(() => {
    if ((isEditMode || isAssignMode) && id) {
      dispatch(fetchRideById(id));
    }
    
    dispatch(fetchDrivers());
    dispatch(fetchVehicles());
    dispatch(fetchCustomers());
    dispatch(fetchRideTemplates());
    dispatch(fetchMedicalTransportOrders());
  }, [dispatch, id, isEditMode, isAssignMode]);
  
  
  useEffect(() => {
    if (currentRide && (isEditMode || isAssignMode)) {
      setFormData({
        ...formData,
        customer_id: currentRide.customerId || '',
        customer_name: currentRide.customerName || '',
        pickup_address: currentRide.start || '',
        pickup_location: {
          latitude: currentRide.startLatitude || null,
          longitude: currentRide.startLongitude || null
        },
        destination_address: currentRide.destination || '',
        destination_location: {
          latitude: currentRide.destinationLatitude || null,
          longitude: currentRide.destinationLongitude || null
        },
        order_type: currentRide.orderType?.toString() || '2',
        scheduled_time: currentRide.scheduledTime ? new Date(currentRide.scheduledTime) : null,
        vehicle_type: currentRide.vehicleType?.toString() || '1',
        special_capabilities: currentRide.specialCapabilities || migrateSpecialVehicleType(currentRide),
        notes: currentRide.notes || '',
        is_krankenfahrt: currentRide.isKrankenfahrt || false,
        medical_transport_order_id: currentRide.medicalTransportOrderId || '',
        is_rechnungsfahrt: currentRide.isRechnungsfahrt || false,
        is_festpreis: currentRide.isFestpreis || false,
        festpreis_eur: currentRide.festpreisEur || '',
        driver_id: currentRide.driverId || '',
        vehicle_id: currentRide.vehicle?.id || ''
      });
    }
  }, [currentRide, isEditMode, isAssignMode]);

  // Fahrt erneut erstellen: Daten aus vorheriger Fahrt vorausfüllen
  useEffect(() => {
    const duplicateRide = location.state?.duplicateRide;
    if (duplicateRide && !isEditMode && !isAssignMode) {
      const isActiveSource = !['completed', 'cancelled', 'cancelled_by_driver'].includes(duplicateRide.status);
      // Bei laufender Quelle (2. Taxi): immer als Sofortbestellung anlegen
      const orderType = isActiveSource ? '2' : (duplicateRide.orderType?.toString() || '2');

      // Für Vorbestellungen: scheduled_time übernehmen wenn in der Zukunft, sonst +1 Stunde
      let scheduledTime = null;
      if (orderType === '1') {
        const originalTime = duplicateRide.scheduledTime
          ? new Date(duplicateRide.scheduledTime)
          : null;
        if (originalTime && originalTime > new Date()) {
          scheduledTime = originalTime;
        } else {
          scheduledTime = new Date(Date.now() + 60 * 60 * 1000);
        }
      }

      // Für Daueraufträge: Standardwerte setzen, Wochentage und Zeit übernehmen
      let recurringStartDate = null;
      let recurringEndDate = null;
      let recurringTime = null;
      let recurringWeekdays = [];
      if (orderType === '3') {
        recurringStartDate = new Date(Date.now() + 24 * 60 * 60 * 1000);
        recurringEndDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
        recurringTime = duplicateRide.recurringTime
          ? new Date(duplicateRide.recurringTime)
          : new Date(new Date().setHours(8, 0, 0, 0));
        recurringWeekdays = duplicateRide.recurringWeekdays || [];
      }

      setFormData(prev => ({
        ...prev,
        customer_id: duplicateRide.customerId || '',
        customer_name: duplicateRide.customerName || '',
        customer_phone: duplicateRide.customerPhone || '',
        pickup_address: duplicateRide.start || '',
        pickup_location: {
          latitude: duplicateRide.startLatitude || null,
          longitude: duplicateRide.startLongitude || null
        },
        destination_address: duplicateRide.destination || '',
        destination_location: {
          latitude: duplicateRide.destinationLatitude || null,
          longitude: duplicateRide.destinationLongitude || null
        },
        order_type: orderType,
        vehicle_type: duplicateRide.vehicleType?.toString() || '1',
        special_capabilities: duplicateRide.specialCapabilities || migrateSpecialVehicleType(duplicateRide),
        notes: duplicateRide.notes || '',
        is_krankenfahrt: duplicateRide.isKrankenfahrt || false,
        medical_transport_order_id: duplicateRide.medicalTransportOrderId || '',
        is_rechnungsfahrt: duplicateRide.isRechnungsfahrt || false,
        is_festpreis: duplicateRide.isFestpreis || false,
        festpreis_eur: duplicateRide.festpreisEur || '',
        scheduled_time: scheduledTime,
        recurring_start_date: recurringStartDate,
        recurring_end_date: recurringEndDate,
        recurring_time: recurringTime,
        recurring_weekdays: recurringWeekdays,
        // Fahrer und Fahrzeug werden NICHT übernommen
        driver_id: '',
        vehicle_id: ''
      }));
    }
  }, [location.state, isEditMode, isAssignMode]);
  
  useEffect(() => {
    // Filter available drivers (only those with 'available' status)
    if (drivers && drivers.length > 0) {
      // Für Sofortbestellungen: nur Online-Fahrer MIT zugewiesenem Fahrzeug
      const onlineDrivers = drivers.filter(driver => {
        const isCurrentDriver = isAssignMode && currentRide && driver.id === currentRide.driverId;
        const hasVehicle = !!(driver.vehicle || driver.car);
        return (driver.isOnline && hasVehicle) || isCurrentDriver;
      });
      setAvailableDrivers(onlineDrivers);

      // Speichere alle Fahrer für Vorbestellungen (keine Fahrzeug-Einschränkung)
      setAllDrivers(drivers);
    }
    
    // Fahrzeuge: Im Zuweisungsmodus alle Fahrzeuge anzeigen, sonst nur aktive/verfügbare
    if (vehicles && vehicles.length > 0) {
      if (isAssignMode) {
        setAvailableVehicles(vehicles);
      } else {
        const filteredVehicles = vehicles.filter(vehicle =>
          (vehicle.status === 'active' || vehicle.status === 'available') ||
          (currentRide && vehicle.id === currentRide.vehicle?.id)
        );
        setAvailableVehicles(filteredVehicles);
      }
    }
  }, [drivers, vehicles, isAssignMode, currentRide]);

  const selectedMedicalTransportOrder = useMemo(
    () => medicalTransportOrders.find(order => order.id === formData.medical_transport_order_id) || null,
    [medicalTransportOrders, formData.medical_transport_order_id]
  );

  const medicalTransportOrderCheck = useMemo(() => {
    if (!formData.is_krankenfahrt || !selectedMedicalTransportOrder) {
      return { severity: 'info', messages: [], plannedCount: 0, remainingRides: null };
    }

    const messages = [];
    let severity = 'success';
    const validFrom = startOfDay(selectedMedicalTransportOrder.validFrom);
    const validUntil = endOfDay(selectedMedicalTransportOrder.validUntil);
    const plannedCount = formData.order_type === '3'
      ? countRecurringDates(formData.recurring_start_date, formData.recurring_end_date, formData.recurring_weekdays)
      : 1;
    const maxRides = Number(selectedMedicalTransportOrder.maxRides) || 0;
    const usedRides = Number(selectedMedicalTransportOrder.usedRides) || 0;
    const alreadyPlannedRides = Number(selectedMedicalTransportOrder.plannedRides) || 0;
    const remainingRides = maxRides > 0 ? maxRides - usedRides - alreadyPlannedRides : null;

    const markWarning = () => {
      severity = severity === 'error' ? 'error' : 'warning';
    };

    const markError = () => {
      severity = 'error';
    };

    if (selectedMedicalTransportOrder.approvalRequired !== false &&
        selectedMedicalTransportOrder.approvalStatus !== 'approved') {
      markWarning();
      messages.push('Genehmigung ist noch nicht als genehmigt markiert.');
    }

    if (selectedMedicalTransportOrder.documentStatus !== 'complete') {
      markWarning();
      messages.push('Dokumentstatus ist noch nicht vollständig.');
    }

    if (remainingRides !== null && plannedCount > remainingRides) {
      markError();
      messages.push(`Kontingent reicht nicht aus: ${plannedCount} neue Fahrt(en), ${Math.max(remainingRides, 0)} verbleibend.`);
    }

    const firstRideDate = formData.order_type === '3'
      ? startOfDay(formData.recurring_start_date)
      : toDateOrNull(formData.scheduled_time) || new Date();
    const lastRideDate = formData.order_type === '3'
      ? endOfDay(formData.recurring_end_date)
      : toDateOrNull(formData.scheduled_time) || new Date();

    if (validFrom && firstRideDate && firstRideDate < validFrom) {
      markError();
      messages.push(`Fahrt liegt vor dem Verordnungsbeginn (${formatDate(selectedMedicalTransportOrder.validFrom)}).`);
    }

    if (validUntil && lastRideDate && lastRideDate > validUntil) {
      markError();
      messages.push(`Fahrt liegt nach dem Verordnungsende (${formatDate(selectedMedicalTransportOrder.validUntil)}).`);
    }

    if (messages.length === 0) {
      messages.push(`Verordnung passt zum Zeitraum. ${remainingRides === null ? 'Kein Kontingent hinterlegt.' : `${Math.max(remainingRides, 0)} Fahrt(en) verbleiben vor dieser Planung.`}`);
    }

    return { severity, messages, plannedCount, remainingRides };
  }, [
    formData.is_krankenfahrt,
    formData.order_type,
    formData.recurring_end_date,
    formData.recurring_start_date,
    formData.recurring_weekdays,
    formData.scheduled_time,
    selectedMedicalTransportOrder
  ]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...formData, [name]: value };

    // Spezialfähigkeiten zurücksetzen wenn Fahrzeugtyp wechselt
    if (name === 'vehicle_type') {
      updatedData.special_capabilities = [];
    }

    setFormData(updatedData);
    
    // Reset errors when field changes
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const handleMedicalTransportOrderChange = (event, order) => {
    setFormData(prev => {
      if (!order) {
        return {
          ...prev,
          medical_transport_order_id: ''
        };
      }

      const requiredCapabilities = Array.isArray(order.requiredCapabilities) ? order.requiredCapabilities : [];
      const nextCapabilities = [...new Set([...(prev.special_capabilities || []), ...requiredCapabilities])];

      return {
        ...prev,
        medical_transport_order_id: order.id,
        customer_id: prev.customer_id || order.customerId || '',
        customer_name: prev.customer_name || order.patientName || '',
        customer_phone: prev.customer_phone || order.patientPhone || '',
        pickup_address: prev.pickup_address || order.pickupAddress || '',
        pickup_location: prev.pickup_location?.latitude
          ? prev.pickup_location
          : {
              latitude: order.pickupLatitude || null,
              longitude: order.pickupLongitude || null
            },
        destination_address: prev.destination_address || order.destinationAddress || '',
        destination_location: prev.destination_location?.latitude
          ? prev.destination_location
          : {
              latitude: order.destinationLatitude || null,
              longitude: order.destinationLongitude || null
            },
        vehicle_type: prev.vehicle_type || String(order.defaultVehicleType || 1),
        special_capabilities: nextCapabilities,
        notes: prev.notes || order.defaultNotes || ''
      };
    });

    setFormErrors(prev => ({
      ...prev,
      medical_transport_order_id: '',
      customer_id: '',
      customer_name: '',
      pickup_address: '',
      destination_address: ''
    }));
  };
  
  const handleOrderTypeChange = (e) => {
    const orderType = e.target.value;
    setFormData({
      ...formData,
      order_type: orderType,
      // Wenn Vorbestellung, aber kein Zeitpunkt gesetzt ist, setze einen Standardwert (1 Stunde in der Zukunft)
      scheduled_time: orderType === '1' && !formData.scheduled_time ? 
        new Date(new Date().getTime() + 60 * 60 * 1000) : formData.scheduled_time,
      // Wenn Dauerauftrag, setze Standardwerte für wiederkehrende Termine
      recurring_start_date: orderType === '3' && !formData.recurring_start_date ?
        new Date(new Date().getTime() + 24 * 60 * 60 * 1000) : formData.recurring_start_date, // Morgen
      recurring_end_date: orderType === '3' && !formData.recurring_end_date ?
        new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000) : formData.recurring_end_date, // In 30 Tagen
      recurring_time: orderType === '3' && !formData.recurring_time ?
        new Date(new Date().setHours(8, 0, 0, 0)) : formData.recurring_time // 08:00 Uhr
    });
  };
  
  const handleScheduledTimeChange = (date) => {
    if (!date) {
      setFormData({
        ...formData,
        scheduled_time: null
      });
    } else {
      // Stelle sicher, dass das Datum im ISO-Format gespeichert wird
      const formattedDate = new Date(date);
      
      setFormData({
        ...formData,
        scheduled_time: formattedDate
      });
    }
    
    if (formErrors.scheduled_time) {
      setFormErrors({
        ...formErrors,
        scheduled_time: ''
      });
    }
  };
  
  // Handler für Dauerauftrag-Felder
  const handleRecurringStartDateChange = (date) => {
    setFormData({
      ...formData,
      recurring_start_date: date
    });
    
    if (formErrors.recurring_start_date) {
      setFormErrors({
        ...formErrors,
        recurring_start_date: ''
      });
    }
  };
  
  const handleRecurringEndDateChange = (date) => {
    setFormData({
      ...formData,
      recurring_end_date: date
    });
    
    if (formErrors.recurring_end_date) {
      setFormErrors({
        ...formErrors,
        recurring_end_date: ''
      });
    }
  };
  
  const handleRecurringTimeChange = (date) => {
    setFormData({
      ...formData,
      recurring_time: date
    });
    
    if (formErrors.recurring_time) {
      setFormErrors({
        ...formErrors,
        recurring_time: ''
      });
    }
  };
  
  const handleWeekdayChange = (dayIndex) => {
    const updatedWeekdays = formData.recurring_weekdays.includes(dayIndex)
      ? formData.recurring_weekdays.filter(day => day !== dayIndex)
      : [...formData.recurring_weekdays, dayIndex].sort();
    
    setFormData({
      ...formData,
      recurring_weekdays: updatedWeekdays
    });
    
    if (formErrors.recurring_weekdays) {
      setFormErrors({
        ...formErrors,
        recurring_weekdays: ''
      });
    }
  };
  
  const handleDriverChange = (event, driver) => {
    const updatedData = {
      ...formData,
      driver_id: driver ? driver.id : ''
    };

    // Fahrzeug automatisch vom Fahrer übernehmen (wenn vorhanden)
    if (driver && (driver.vehicle || driver.car)) {
      const vehicleId = driver.vehicle?.id || driver.car?.vehicleId || '';
      updatedData.vehicle_id = vehicleId;
    } else if (!driver) {
      updatedData.vehicle_id = '';
    }

    setFormData(updatedData);

    if (formErrors.driver_id || formErrors.vehicle_id) {
      setFormErrors({
        ...formErrors,
        driver_id: '',
        vehicle_id: ''
      });
    }
  };
  
  const handleVehicleChange = (event, vehicle) => {
    setFormData({
      ...formData,
      vehicle_id: vehicle ? vehicle.id : ''
    });
    
    if (formErrors.vehicle_id) {
      setFormErrors({
        ...formErrors,
        vehicle_id: ''
      });
    }
  };
  
  // Filter-Option für Autocomplete
  const filter = createFilterOptions();
  
  const handleCustomerChange = (event, newValue) => {
    if (typeof newValue === 'string') {
      // Wenn eine Texteingabe gemacht wurde (kein Kunde aus der Liste ausgewählt)
      setFormData({
        ...formData,
        customer_id: '',
        customer_name: newValue || ''
      });
      // Leerer String → kein neuer Kunde, sondern Laufkunde-Fallback
      setCreateNewCustomer(!!newValue && newValue.trim().length > 0);
    } else if (newValue && newValue.inputValue) {
      // Wenn "Neuen Kunden erstellen" ausgewählt wurde
      setFormData({
        ...formData,
        customer_id: '',
        customer_name: newValue.inputValue || ''
      });
      setCreateNewCustomer(true);
    } else {
      // Wenn ein existierender Kunde ausgewählt wurde
      setFormData({
        ...formData,
        customer_id: newValue?.id || '',
        customer_name: newValue?.name || newValue?.user?.name || '',
        customer_phone: newValue?.phone || newValue?.user?.phoneNumber || ''
      });
      setCreateNewCustomer(false);
    }
    
    if (formErrors.customer_id) {
      setFormErrors({
        ...formErrors,
        customer_id: ''
      });
    }
  };
  
  const handleCustomerNameChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      customer_name: value
    });
    
    if (formErrors.customer_name) {
      setFormErrors({
        ...formErrors,
        customer_name: ''
      });
    }
  };
  
  const handleCustomerPhoneChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      customer_phone: value
    });
    
    if (formErrors.customer_phone) {
      setFormErrors({
        ...formErrors,
        customer_phone: ''
      });
    }
  };

  const handleSwapAddresses = () => {
    setFormData(prev => ({
      ...prev,
      pickup_address: prev.destination_address,
      pickup_location: prev.destination_location,
      destination_address: prev.pickup_address,
      destination_location: prev.pickup_location
    }));
    setFormErrors(prev => ({
      ...prev,
      pickup_address: '',
      destination_address: ''
    }));
  };

  const calculateEstimatedTimeMinutes = async (pickupLocation, destinationLocation, departureTime = null) => {
    if (!pickupLocation || !destinationLocation || 
        !pickupLocation.latitude || !pickupLocation.longitude ||
        !destinationLocation.latitude || !destinationLocation.longitude) {
      console.warn('Fehlende Koordinaten für Fahrzeitberechnung');
      return null;
    }

    try {
      const directionsService = new window.google.maps.DirectionsService();
      
      const request = {
        origin: { lat: pickupLocation.latitude, lng: pickupLocation.longitude },
        destination: { lat: destinationLocation.latitude, lng: destinationLocation.longitude },
        travelMode: window.google.maps.TravelMode.DRIVING,
        drivingOptions: {
          departureTime: departureTime || new Date(),
          trafficModel: window.google.maps.TrafficModel.BEST_GUESS
        }
      };

      const result = await new Promise((resolve, reject) => {
        directionsService.route(request, (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            resolve(result);
          } else {
            reject(new Error(`Directions API Fehler: ${status}`));
          }
        });
      });

      // Hole die geschätzte Fahrtdauer (mit Verkehr wenn verfügbar)
      const route = result.routes[0];
      if (route && route.legs && route.legs[0]) {
        const leg = route.legs[0];
        // duration_in_traffic ist nur verfügbar wenn departureTime gesetzt ist
        const durationInSeconds = leg.duration_in_traffic
          ? leg.duration_in_traffic.value
          : leg.duration.value;

        const durationInMinutes = Math.ceil(durationInSeconds / 60);
        const distanceKm = leg.distance ? leg.distance.value / 1000 : null;

        console.log('Berechnete Fahrtdauer:', {
          route: `${pickupLocation.latitude},${pickupLocation.longitude} -> ${destinationLocation.latitude},${destinationLocation.longitude}`,
          durationInMinutes,
          distanceKm,
          withTraffic: !!leg.duration_in_traffic,
          departureTime: departureTime || 'Jetzt'
        });

        return { durationInMinutes, distanceKm };
      }

      return null;
    } catch (error) {
      console.error('Fehler bei der Fahrzeitberechnung:', error);
      return null;
    }
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = { ...formErrors };
    newErrors.medical_transport_order_id = '';
    
    if (!isAssignMode) {
      // Kundenfeld ist optional: Bleibt es leer, wird automatisch ein "Laufkunde"
      // (Telefonkunde ohne eigenen Datensatz) als Kunde der Fahrt verwendet.
      // Wenn ein neuer Telefonkunde erstellt werden soll, muss ein Name angegeben sein.
      if (createNewCustomer && !formData.customer_name.trim()) {
        newErrors.customer_name = 'Bitte geben Sie einen Namen für den neuen Telefonkunden ein';
        valid = false;
      }
      
      if (!formData.pickup_address) {
        newErrors.pickup_address = 'Abholadresse ist erforderlich';
        valid = false;
      }
      
      // Zieladresse ist optional, aber ein Hinweis wird im UI angezeigt
      
      // Validiere scheduledTime für Vorbestellungen
      if (formData.order_type === '1' && !formData.scheduled_time) {
        newErrors.scheduled_time = 'Bitte geben Sie einen Zeitpunkt für die Vorbestellung an';
        valid = false;
      }
      
      if (formData.order_type === '1' && formData.scheduled_time && new Date(formData.scheduled_time) < new Date()) {
        newErrors.scheduled_time = 'Der Zeitpunkt muss in der Zukunft liegen';
        valid = false;
      }
      
      // Validiere Dauerauftrag-Felder
      if (formData.order_type === '3') {
        if (!formData.recurring_start_date) {
          newErrors.recurring_start_date = 'Bitte geben Sie ein Startdatum für den Dauerauftrag an';
          valid = false;
        }
        
        if (!formData.recurring_end_date) {
          newErrors.recurring_end_date = 'Bitte geben Sie ein Enddatum für den Dauerauftrag an';
          valid = false;
        }
        
        if (!formData.recurring_time) {
          newErrors.recurring_time = 'Bitte geben Sie eine Uhrzeit für den Dauerauftrag an';
          valid = false;
        }
        
        if (formData.recurring_weekdays.length === 0) {
          newErrors.recurring_weekdays = 'Bitte wählen Sie mindestens einen Wochentag aus';
          valid = false;
        }
        
        if (formData.recurring_start_date && formData.recurring_end_date && 
            new Date(formData.recurring_end_date) <= new Date(formData.recurring_start_date)) {
          newErrors.recurring_end_date = 'Das Enddatum muss nach dem Startdatum liegen';
          valid = false;
        }
        
        if (formData.recurring_start_date && new Date(formData.recurring_start_date) < new Date()) {
          newErrors.recurring_start_date = 'Das Startdatum muss in der Zukunft liegen';
          valid = false;
        }
      }

      // Für Sofortbestellungen mit Direktzuweisung: Fahrer muss ein Fahrzeug haben
      if (formData.driver_id && formData.order_type !== '1' && formData.order_type !== '3') {
        const selectedDriver = drivers.find(d => d.id === formData.driver_id);
        if (selectedDriver && !selectedDriver.vehicle && !selectedDriver.car) {
          newErrors.driver_id = 'Diesem Fahrer ist kein Fahrzeug zugewiesen. Für Sofortbestellungen muss ein Fahrzeug vorhanden sein.';
          valid = false;
        }
      }

      if (formData.is_krankenfahrt && selectedMedicalTransportOrder && medicalTransportOrderCheck.severity === 'error') {
        newErrors.medical_transport_order_id = medicalTransportOrderCheck.messages.join(' ');
        valid = false;
      }
    } else {
      // Validate ride assignment fields
      if (!formData.driver_id) {
        newErrors.driver_id = 'Bitte wählen Sie einen Fahrer aus';
        valid = false;
      } else if (currentRide?.orderType !== 1 && currentRide?.orderType !== 3) {
        // Für Sofortbestellungen: Fahrer muss ein Fahrzeug zugewiesen haben
        const selectedDriver = drivers.find(d => d.id === formData.driver_id);
        if (selectedDriver && !selectedDriver.vehicle && !selectedDriver.car) {
          newErrors.driver_id = 'Diesem Fahrer ist kein Fahrzeug zugewiesen. Für Sofortbestellungen muss ein Fahrzeug vorhanden sein.';
          valid = false;
        }
      }

      // Fahrzeug nur bei Sofortbestellungen als Pflichtfeld prüfen
      if (currentRide?.orderType !== 1 && currentRide?.orderType !== 3 && !formData.vehicle_id) {
        newErrors.vehicle_id = 'Bitte wählen Sie ein Fahrzeug aus';
        valid = false;
      }
    }
    
    setFormErrors(newErrors);
    return valid;
  };
  
  const handleTemplateSelect = (event, template) => {
    if (!template) return;
    
    setFormData(prevData => ({
      ...prevData,
      customer_name: template.customer_name,
      customer_phone: template.customer_phone || '',
      customer_id: template.customer_id || '',
      pickup_address: template.pickup_address,
      pickup_location: template.pickup_location || { latitude: null, longitude: null },
      destination_address: template.destination_address,
      destination_location: template.destination_location || { latitude: null, longitude: null },
      vehicle_type: template.vehicle_type,
      order_type: template.order_type || '2'
    }));
  };

  // Funktion zum Generieren aller Einzelfahrten aus einem Dauerauftrag
  const generateRecurringRides = (recurringOrderData) => {
    const rides = [];
    const startDate = new Date(formData.recurring_start_date);
    const endDate = new Date(formData.recurring_end_date);
    const selectedWeekdays = formData.recurring_weekdays;
    
    // Generiere eine eindeutige ID für alle Fahrten dieses Dauerauftrags
    const recurringOrderId = `recurring_${Date.now()}`;
    
    // Setze die Zeit auf den Start des Tages für korrekte Vergleiche
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    const current = new Date(startDate);
    
    while (current <= endDate) {
      const currentWeekday = current.getDay();
      
      if (selectedWeekdays.includes(currentWeekday)) {
        // Erstelle die scheduledTime für diesen Tag
        const rideDateTime = new Date(current);
        const timeOnly = new Date(formData.recurring_time);
        rideDateTime.setHours(timeOnly.getHours(), timeOnly.getMinutes(), 0, 0);
        
        // Erstelle Fahrtdaten für diesen Termin
        const rideData = {
          ...recurringOrderData,
          scheduledTime: rideDateTime.toISOString(),
          status: 'scheduled', // Wiederkehrende Fahrten sind immer geplant
          orderType: 1, // Behandle wie Vorbestellung
          recurringOrderId: recurringOrderId, // Verwende die gemeinsame ID für alle Fahrten
          isRecurring: true
        };
        
        rides.push(rideData);
      }
      
      // Gehe zum nächsten Tag
      current.setDate(current.getDate() + 1);
    }
    
    return rides;
  };

  const handleSubmit = async (e, skipDestinationCheck = false) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!skipDestinationCheck && !isAssignMode && !isEditMode && !formData.destination_address) {
      if (validateForm()) {
        setShowNoDestinationDialog(true);
      }
      return;
    }

    if (validateForm()) {
      if (isAssignMode) {
        // Assign driver and vehicle to ride
        dispatch(assignRide({
          rideId: id,
          driverId: formData.driver_id
        })).then((result) => {
          if (!result.error) {
            navigate(`/fahrten/${id}`);
          }
        });
      } else if (isEditMode) {
        // Berechne geschätzte Fahrtdauer bevor wir die Fahrt aktualisieren
        let estimatedTimeMinutes = null;
        let distanceKm = null;
        if (formData.pickup_location && formData.destination_location) {
          setIsCalculatingRoute(true);
          try {
            const routeResult = await calculateEstimatedTimeMinutes(
              formData.pickup_location,
              formData.destination_location,
              currentRide.orderType === 1 && formData.scheduled_time ? formData.scheduled_time : null
            );
            if (routeResult) {
              estimatedTimeMinutes = routeResult.durationInMinutes;
              distanceKm = routeResult.distanceKm;
            }
          } finally {
            setIsCalculatingRoute(false);
          }
        }
        
        const medicalTransportSnapshot = formData.is_krankenfahrt
          ? buildMedicalTransportSnapshot(selectedMedicalTransportOrder)
          : null;

        // Fahrt aktualisieren mit aktuellen Formular-Daten
        const updateData = {
          rideId: id,
          // Grundlegende Felder
          start: formData.pickup_address,
          startLatitude: formData.pickup_location?.latitude || 0,
          startLongitude: formData.pickup_location?.longitude || 0,
          destination: formData.destination_address,
          destinationLatitude: formData.destination_location?.latitude || 0,
          destinationLongitude: formData.destination_location?.longitude || 0,
          vehicleType: parseInt(formData.vehicle_type, 10),
          specialCapabilities: formData.special_capabilities || [],
          notes: formData.special_capabilities?.length > 0
            ? (formData.notes.trim() ? `[${formData.special_capabilities.map(c => SPECIAL_CAPABILITIES[c] || c).join(', ')}] ${formData.notes.trim()}` : `[${formData.special_capabilities.map(c => SPECIAL_CAPABILITIES[c] || c).join(', ')}]`)
            : (formData.notes.trim() || null),
          isKrankenfahrt: formData.is_krankenfahrt,
          medicalTransportOrderId: formData.is_krankenfahrt ? (formData.medical_transport_order_id || null) : null,
          medicalTransportSnapshot,
          medicalTransportBillingStatus: formData.is_krankenfahrt ? 'not_ready' : null,
          medicalTransportProofStatus: formData.is_krankenfahrt ? 'missing' : null,
          isRechnungsfahrt: formData.is_rechnungsfahrt,
          isFestpreis: formData.is_festpreis && formData.festpreis_eur ? true : false,
          ...(formData.is_festpreis && formData.festpreis_eur ? {
            festpreisEur: parseFloat(formData.festpreis_eur),
            festpreisLabel: `Festpreis ${parseFloat(formData.festpreis_eur).toFixed(2)} €`,
            estimatedEarningEur: parseFloat(formData.festpreis_eur),
          } : {
            festpreisEur: null,
            festpreisLabel: null,
          }),
          // Aktualisiere weitere Felder nur, wenn sie sich geändert haben
          ...(currentRide.customerId !== formData.customer_id ? { customerId: formData.customer_id } : {}),
          ...(currentRide.customerName !== formData.customer_name ? { customerName: formData.customer_name } : {}),
          ...(currentRide.customerPhone !== formData.customer_phone ? { customerPhone: formData.customer_phone } : {}),
          // Fahrerzuweisung wird nicht im Bearbeitungsmodus geändert — dafür gibt es den Zuweisungsmodus
          // Füge geschätzte Fahrtdauer hinzu, wenn berechnet
          ...(estimatedTimeMinutes !== null ? { estimatedTimeMinutes } : {}),
          ...(distanceKm !== null ? { distanceKm: parseFloat(distanceKm.toFixed(2)) } : {})
        };
        
        // Für Vorbestellungen, Zeitpunkt aktualisieren
        if (currentRide.orderType === 1 && formData.scheduled_time) {
          updateData.scheduledTime = formData.scheduled_time.toISOString();
        }
        
        dispatch(updateRide(updateData)).then((result) => {
          if (!result.error) {
            navigate(`/fahrten/${id}`);
          }
        });
      } else {
        // Prüfen, ob ein neuer Kunde erstellt werden muss
        let customerId = formData.customer_id;
        let customerName = formData.customer_name;

        // Laufkunde-Fallback: Disponent hat weder einen Kunden gewählt
        // noch einen Namen für einen neuen Telefonkunden eingegeben.
        const isLaufkunde = !createNewCustomer && !customerId;

        if (createNewCustomer) {
          try {
            // Erstelle neuen Telefonkunden (konsistent mit dem Telefonkunden-Konzept)
            const newCustomerData = {
              isPhoneCustomer: true, // Markiert als Telefonkunde
              name: formData.customer_name,
              phoneNumber: formData.customer_phone || '',
              // Keine Adresse hier, da wir diese im Fahrtformular nicht abfragen
              // Das ist konsistent mit dem vereinfachten Telefonkunden-Formular
            };

            const resultAction = await dispatch(createCustomer(newCustomerData));

            if (createCustomer.fulfilled.match(resultAction)) {
              customerId = resultAction.payload.id;
              customerName = resultAction.payload.user.name;
            } else {
              // Wenn der Kunde nicht erstellt werden konnte, Fehler anzeigen
              console.error('Fehler beim Erstellen des Kunden:', resultAction.error);
              return;
            }
          } catch (error) {
            console.error('Fehler beim Erstellen des Kunden:', error);
            return;
          }
        } else if (isLaufkunde) {
          customerId = null;
          customerName = DEFAULT_PHONE_CUSTOMER_NAME;
        }
        
        // Berechne geschätzte Fahrtdauer bevor wir die Fahrt erstellen
        let estimatedTimeMinutes = null;
        let distanceKm = null;
        if (formData.pickup_location && formData.destination_location) {
          setIsCalculatingRoute(true);
          try {
            const routeResult = await calculateEstimatedTimeMinutes(
              formData.pickup_location,
              formData.destination_location,
              formData.order_type === '1' && formData.scheduled_time ? formData.scheduled_time : null
            );
            if (routeResult) {
              estimatedTimeMinutes = routeResult.durationInMinutes;
              distanceKm = routeResult.distanceKm;
            }
          } finally {
            setIsCalculatingRoute(false);
          }
        }
        
        // Kundentyp bestimmen: Telefonkunde oder App-Kunde
        const isPhoneCustomer = (createNewCustomer || isLaufkunde)
          ? true  // Neue Kunden im Portal und Laufkunden sind immer Telefonkunden
          : (customers.find(c => c.id === customerId)?.user?.isPhoneCustomer || false);

        const medicalTransportSnapshot = formData.is_krankenfahrt
          ? buildMedicalTransportSnapshot(selectedMedicalTransportOrder)
          : null;

        // Bestimme Status basierend auf Bestelltyp und ob ein Fahrer ausgewählt wurde
        let status;
        if (formData.driver_id) {
          // Wenn ein Fahrer ausgewählt wurde, setze Status entsprechend dem Bestelltyp
          status = formData.order_type === '1' ? 'pre_accepted' : 'dispatcher_pending';
        } else {
          // Wenn kein Fahrer ausgewählt wurde, verwende den Standard-Status
          status = formData.order_type === '1' ? 'scheduled' : 'open';
        }
        
        // Erstelle Daten für die Fahrt basierend auf dem Bestelltyp
        const commonRideData = {
          customerId: customerId || null,
          customerName: customerName || DEFAULT_PHONE_CUSTOMER_NAME,
          customerPhone: formData.customer_phone || null, // Telefonnummer des Kunden hinzufügen
          start: formData.pickup_address,
          startLatitude: formData.pickup_location?.latitude || 0,
          startLongitude: formData.pickup_location?.longitude || 0,
          destination: formData.destination_address,
          destinationLatitude: formData.destination_location?.latitude || 0,
          destinationLongitude: formData.destination_location?.longitude || 0,
          vehicleType: parseInt(formData.vehicle_type, 10),
          specialCapabilities: formData.special_capabilities || [],
          orderType: parseInt(formData.order_type, 10),
          status: status,
          notes: formData.special_capabilities?.length > 0
            ? (formData.notes.trim() ? `[${formData.special_capabilities.map(c => SPECIAL_CAPABILITIES[c] || c).join(', ')}] ${formData.notes.trim()}` : `[${formData.special_capabilities.map(c => SPECIAL_CAPABILITIES[c] || c).join(', ')}]`)
            : (formData.notes.trim() || null),
          isKrankenfahrt: formData.is_krankenfahrt,
          medicalTransportOrderId: formData.is_krankenfahrt ? (formData.medical_transport_order_id || null) : null,
          medicalTransportSnapshot,
          medicalTransportBillingStatus: formData.is_krankenfahrt ? 'not_ready' : null,
          medicalTransportProofStatus: formData.is_krankenfahrt ? 'missing' : null,
          isRechnungsfahrt: formData.is_rechnungsfahrt,
          isPhoneCustomer: isPhoneCustomer,
          ...(formData.is_festpreis && formData.festpreis_eur ? {
            isFestpreis: true,
            festpreisEur: parseFloat(formData.festpreis_eur),
            festpreisLabel: `Festpreis ${parseFloat(formData.festpreis_eur).toFixed(2)} €`,
            estimatedEarningEur: parseFloat(formData.festpreis_eur),
          } : {}),
          // Füge geschätzte Fahrtdauer hinzu, wenn berechnet
          ...(estimatedTimeMinutes !== null ? { estimatedTimeMinutes } : {}),
          ...(distanceKm !== null ? { distanceKm: parseFloat(distanceKm.toFixed(2)) } : {})
        };

        // Spezifische Daten je nach Bestelltyp
        let specificRideData = {};
        
        if (formData.order_type === '1') { // Vorbestellung
          // Hier stellen wir sicher, dass das Datum korrekt formatiert wird
          // und als ISO-String für die API gespeichert wird
          const scheduledTime = formData.scheduled_time;
          
          specificRideData = {
            scheduledTime: scheduledTime.toISOString(),
          };
        } else if (formData.order_type === '3') { // Dauerauftrag
          // Für Daueraufträge generieren wir alle Einzelfahrten
          const recurringRides = generateRecurringRides(commonRideData);
          
          // Erstelle alle Fahrten in Serie
          try {
            let createdRidesCount = 0;
            const errors = [];
            
            for (const rideData of recurringRides) {
              try {
                const result = await dispatch(createRide(rideData));
                if (!result.error) {
                  createdRidesCount++;
                } else {
                  errors.push(`Fehler bei Fahrt am ${new Date(rideData.scheduledTime).toLocaleDateString('de-DE')}: ${result.error}`);
                }
              } catch (error) {
                errors.push(`Fehler bei Fahrt am ${new Date(rideData.scheduledTime).toLocaleDateString('de-DE')}: ${error.message}`);
              }
            }
            
            if (errors.length === 0) {
              // Alle Fahrten erfolgreich erstellt
              alert(`Dauerauftrag erfolgreich erstellt! ${createdRidesCount} Fahrten wurden geplant.`);
              navigate('/fahrten');
              return; // Beende die Funktion hier für Daueraufträge
            } else {
              // Teilweise Fehler
              alert(`Dauerauftrag teilweise erstellt. ${createdRidesCount} von ${recurringRides.length} Fahrten wurden erfolgreich erstellt.\n\nFehler:\n${errors.join('\n')}`);
              navigate('/fahrten');
              return; // Beende die Funktion hier für Daueraufträge
            }
          } catch (error) {
            alert(`Fehler beim Erstellen des Dauerauftrags: ${error.message}`);
            return; // Beende die Funktion bei Fehlern
          }
        }
        
        // Füge Fahrer-Daten hinzu, wenn ein Fahrer ausgewählt wurde
        if (formData.driver_id) {
          // Finde den ausgewählten Fahrer aus den verfügbaren Fahrern
          const selectedDriver = (formData.order_type === '1' || formData.order_type === '3')
            ? allDrivers.find(d => d.id === formData.driver_id)
            : availableDrivers.find(d => d.id === formData.driver_id);
          
          if (selectedDriver) {
            specificRideData = {
              ...specificRideData,
              driverId: selectedDriver.id,
              driverName: selectedDriver.name || selectedDriver.user?.name || 'Unbekannt',
              driverProfilePic: selectedDriver.profilePic || selectedDriver.user?.profile_picture || '',
              driverRating: selectedDriver.rating || null,
              driverRatingCount: selectedDriver.ratingCount || null,
              assignedByDispatcher: true, // Cloud Function: Push an Fahrer senden
            };
            
            // Füge Zeitstempelfeld für Fahrerannahme hinzu (bei Vor- und Dauerbestellungen)
            // Bei Sofortbestellungen wird der Timestamp erst gesetzt wenn der Fahrer annimmt
            if (formData.order_type === '1' || formData.order_type === '3') {
              specificRideData.acceptedAt = new Date().toISOString();
            }
            
            // Fahrzeugdaten nur bei Sofortbestellungen hinzufügen
            // Bei Vor-/Dauerbestellungen werden die Fahrzeugdaten bei Fahrtantritt durch die Mobile-App gesetzt
            if (formData.order_type !== '1' && formData.order_type !== '3') {
              if (selectedDriver.vehicle) {
                specificRideData.driverVehicleId = selectedDriver.vehicle.id || null;
                specificRideData.driverModel = selectedDriver.vehicle.model || null;
                specificRideData.driverLicensePlate = selectedDriver.vehicle.licensePlate || null;
              } else if (formData.vehicle_id) {
                // Fallback: Fahrzeugdaten aus der Fahrzeug-Auswahl laden
                const selectedVehicle = availableVehicles.find(v => v.id === formData.vehicle_id);
                if (selectedVehicle) {
                  specificRideData.driverVehicleId = selectedVehicle.id;
                  specificRideData.driverModel = selectedVehicle.model || null;
                  specificRideData.driverLicensePlate = selectedVehicle.licensePlate || null;
                }
              }
            }
          }
        }
        
        // Kombiniere die Daten und erstelle die Fahrt
        const rideData = {
          ...commonRideData,
          ...specificRideData
        };
        
        dispatch(createRide(rideData)).then((result) => {
          if (!result.error) {
            // Wenn "Als Vorlage speichern" ausgewählt ist, Fahrtdetails als Vorlage speichern
            if (saveAsTemplate) {
              dispatch(saveRideTemplate({
                customer_name: formData.customer_name,
                customer_phone: formData.customer_phone,
                customer_id: customerId,
                pickup_address: formData.pickup_address,
                pickup_location: formData.pickup_location,
                destination_address: formData.destination_address,
                destination_location: formData.destination_location,
                vehicle_type: formData.vehicle_type,
                order_type: formData.order_type
              }));
            }
            navigate('/fahrten');
          }
        });
      }
    }
  };
  
  const handleCancel = () => {
    if (isEditMode || isAssignMode) {
      navigate(`/fahrten/${id}`);
    } else {
      navigate('/fahrten');
    }
  };
  
  const isLoading = rideLoading || driversLoading || vehiclesLoading || customersLoading;
  
  if (isLoading && (isEditMode || isAssignMode)) {
    return <Loading message="Daten werden geladen..." />;
  }
  
  if (rideError && (isEditMode || isAssignMode)) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Daten"
        message={rideError}
        onRetry={() => dispatch(fetchRideById(id))}
      />
    );
  }
  
  if ((isEditMode || isAssignMode) && currentRide?.status === 'cancelled') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Diese Fahrt wurde storniert und kann nicht mehr bearbeitet oder zugewiesen werden.
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate(`/fahrten/${id}`)}
        >
          Zurück zur Fahrtansicht
        </Button>
      </Box>
    );
  }
  
  if (isAssignMode && !['scheduled', 'open', 'dispatcher_pending', 'pre_accepted', 'accepted'].includes(currentRide?.status)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Diese Fahrt kann nicht mehr zugewiesen werden (Status: {currentRide?.status}).
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate(`/fahrten/${id}`)}
        >
          Zurück zur Fahrtansicht
        </Button>
      </Box>
    );
  }
  
  // Customer-Options erstellen (inkl. "Neuer Kunde" Option)
  const customerOptions = customers;
  
  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
      <GlobalStyle />
      <Box sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 }, overflow: 'visible', pb: { xs: 10, sm: 3 } }}>
      <PageHeader
        title={
          isAssignMode ? (currentRide?.driverId ? 'Fahrer neu zuweisen' : 'Fahrer zuweisen') :
          isEditMode ? 'Fahrt bearbeiten' :
          location.state?.duplicateRide
            ? (['completed', 'cancelled', 'cancelled_by_driver'].includes(location.state.duplicateRide.status)
                ? 'Fahrt erneut erstellen'
                : '2. Taxi anlegen')
            : 'Neue Fahrt anlegen'
        }
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrten', path: '/fahrten' },
          {
            label: isAssignMode ? (currentRide?.driverId ? 'Neu zuweisen' : 'Zuweisen') : isEditMode ? 'Bearbeiten' : 'Neu',
            path: isAssignMode ? `/fahrten/${id}/zuweisen` : 
                  isEditMode ? `/fahrten/${id}/bearbeiten` : 
                  '/fahrten/neu' 
          }
        ]}
        secondaryButton={{
          label: 'Vorlagen verwalten',
          icon: <BookmarkIcon />,
          path: '/vorlagen'
        }}
      />

      {/* Wartezeit-Anzeige – nur beim Erstellen neuer Fahrten */}
      {!isEditMode && !isAssignMode && waitTimes && (
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            mb: 2,
            borderRadius: 2,
            bgcolor: 'grey.100',
            border: '1px solid',
            borderColor: 'grey.300',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5 }}>
            <TimerIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" fontWeight="medium">
              Aktuelle Wartezeit:
            </Typography>
          </Box>
          <Chip
            icon={<CarIcon />}
            label={`PKW ${waitTimes.general}`}
            size="small"
            variant="outlined"
            sx={{ borderColor: 'warning.main', color: 'warning.dark', '& .MuiChip-icon': { color: 'warning.dark' } }}
          />
          {existingVehicleTypes.has(2) && (
            <Chip
              icon={<KombiIcon />}
              label={`Großraum ${waitTimes.grossraum}`}
              size="small"
              variant="outlined"
              sx={{ borderColor: 'info.main', color: 'info.dark', '& .MuiChip-icon': { color: 'info.dark' } }}
            />
          )}
          {existingVehicleTypes.has(3) && (
            <Chip
              icon={<MinibusIcon />}
              label={`Kleinbus 7 ${waitTimes.kleinbus7}`}
              size="small"
              variant="outlined"
              sx={{ borderColor: 'success.main', color: 'success.dark', '& .MuiChip-icon': { color: 'success.dark' } }}
            />
          )}
          {existingVehicleTypes.has(4) && (
            <Chip
              icon={<MinibusIcon />}
              label={`Kleinbus 8 ${waitTimes.kleinbus8}`}
              size="small"
              variant="outlined"
              sx={{ borderColor: 'secondary.main', color: 'secondary.dark', '& .MuiChip-icon': { color: 'secondary.dark' } }}
            />
          )}
        </Paper>
      )}

      <form onSubmit={handleSubmit}>
        {isAssignMode ? (
          // Assignment Form - Fahrtdaten grau/deaktiviert + Fahrerzuweisung aktiv
          <>
            {/* Hinweis bei Neuzuweisung */}
            {isAssignMode && currentRide?.driverId && (
              <Alert
                severity={currentRide.status === 'accepted' ? 'warning' : 'info'}
                sx={{ mb: 2 }}
              >
                {currentRide.status === 'accepted'
                  ? `Achtung: Fahrer ${currentRide.driverName || 'Unbekannt'} hat diese Fahrt bereits angenommen. Bei einer Neuzuweisung wird ihm die Fahrt entzogen und er wird benachrichtigt.`
                  : `Diese Fahrt ist derzeit Fahrer ${currentRide.driverName || 'Unbekannt'} zugewiesen. Bei einer Neuzuweisung wird ihm die Fahrt entzogen und er wird benachrichtigt.`}
              </Alert>
            )}

            {/* Fahrtdaten (deaktiviert) */}
            <FormPaper elevation={2} sx={{ bgcolor: 'grey.50' }}>
              <Typography variant="h6" gutterBottom color="text.secondary">
                Fahrtdaten
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Kunde"
                    value={currentRide?.customerName || 'Unbekannt'}
                    disabled
                    InputProps={{
                      startAdornment: <PersonIcon color="disabled" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Telefon"
                    value={currentRide?.customerPhone || currentRide?.customer?.phone || 'Nicht angegeben'}
                    disabled
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Abholadresse"
                    value={currentRide?.start || ''}
                    disabled
                    InputProps={{
                      startAdornment: <LocationIcon color="disabled" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Zieladresse"
                    value={currentRide?.destination || ''}
                    disabled
                    InputProps={{
                      startAdornment: <LocationIcon color="disabled" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Bestelltyp"
                    value={currentRide?.orderType === 1 ? 'Vorbestellung' : currentRide?.orderType === 3 ? 'Dauerauftrag' : 'Sofortbestellung'}
                    disabled
                    InputProps={{
                      startAdornment: currentRide?.orderType === 1
                        ? <ScheduleIcon color="disabled" sx={{ mr: 1 }} />
                        : <ImmediateIcon color="disabled" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Fahrzeugtyp"
                    value={getVehicleTypeDescription(currentRide?.vehicleType)}
                    disabled
                  />
                </Grid>
                {currentRide?.scheduledTime && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Geplante Zeit"
                      value={new Date(currentRide.scheduledTime).toLocaleString('de-DE', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      disabled
                      InputProps={{
                        startAdornment: <ScheduleIcon color="disabled" sx={{ mr: 1 }} />
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </FormPaper>

            {/* Fahrer zuweisen (aktiv) */}
            <FormPaper elevation={2}>
              <Typography variant="h6" gutterBottom>
                Fahrer zuweisen
              </Typography>

              {currentRide?.driverId && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Diese Fahrt ist bereits <strong>{currentRide.driverName || 'einem Fahrer'}</strong> zugewiesen
                  {currentRide.status === 'dispatcher_pending' && ' (wartet auf Annahme)'}.
                  Bei Neuzuweisung wird der bisherige Fahrer automatisch entfernt und benachrichtigt.
                </Alert>
              )}

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    id="driver_id"
                    options={(currentRide?.orderType === 1 || currentRide?.orderType === 3 || currentRide?.convertedToOpenRide) ? allDrivers : availableDrivers}
                    getOptionLabel={(option) => option.name || option.user?.name || `Fahrer ${option.id}`}
                    value={((currentRide?.orderType === 1 || currentRide?.orderType === 3 || currentRide?.convertedToOpenRide) ? allDrivers : availableDrivers).find(d => d.id === formData.driver_id) || null}
                    onChange={handleDriverChange}
                    renderInput={(params) =>
                      <TextField
                        {...params}
                        label="Fahrer"
                        fullWidth
                        required
                        error={!!formErrors.driver_id}
                        helperText={formErrors.driver_id}
                      />
                    }
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body1">{option.name || option.user?.name || `Fahrer ${option.id}`}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            {option.isOnline ? "Online" : "Offline"} | Rating: {option.rating?.toFixed(1) || "N/A"} | Tel: {option.phone || option.user?.phone || "N/A"}
                          </Typography>
                          {option.vehicle && (
                            <Typography variant="body2" color="textSecondary">
                              Fahrzeug: {option.vehicle.model || 'Unbekannt'} {option.vehicle.licensePlate ? `(${option.vehicle.licensePlate})` : ''}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    )}
                  />
                  <FormHelperText>
                    {((currentRide?.orderType === 1 || currentRide?.orderType === 3 || currentRide?.convertedToOpenRide) ? allDrivers : availableDrivers).length === 0
                      ? 'Keine verfügbaren Fahrer gefunden.'
                      : (currentRide?.orderType === 1 || currentRide?.orderType === 3 || currentRide?.convertedToOpenRide)
                        ? 'Für Vor-/Dauerbestellungen können alle Fahrer zugewiesen werden. Fahrzeugdaten werden bei Fahrtantritt gesetzt.'
                        : 'Für Sofortbestellungen können nur Online-Fahrer mit zugewiesenem Fahrzeug zugewiesen werden.'}
                  </FormHelperText>
                </Grid>

                {/* Fahrzeug: Bei Sofortbestellungen auto-filled & required, bei Vor-/Dauerbestellungen optional */}
                {formData.driver_id && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    {(currentRide?.orderType === 1 || currentRide?.orderType === 3) ? (
                      // Vorbestellung/Dauerauftrag: optionales Fahrzeug-Dropdown
                      <>
                        <Autocomplete
                          id="vehicle_id"
                          options={availableVehicles}
                          getOptionLabel={(option) => `${option.model || 'Unbekannt'} (${option.licensePlate || 'Kein Kennzeichen'})`}
                          value={availableVehicles.find(v => v.id === formData.vehicle_id) || null}
                          onChange={handleVehicleChange}
                          renderInput={(params) =>
                            <TextField
                              {...params}
                              label="Fahrzeug (optional)"
                              fullWidth
                              error={!!formErrors.vehicle_id}
                              helperText={formErrors.vehicle_id || 'Fahrzeugdaten werden bei Fahrtantritt durch die Mobile-App gesetzt.'}
                            />
                          }
                        />
                      </>
                    ) : (
                      // Sofortbestellung: Fahrzeug automatisch vom Fahrer übernommen
                      (() => {
                        const selectedDriver = drivers.find(d => d.id === formData.driver_id);
                        const vehicleInfo = selectedDriver?.vehicle || selectedDriver?.car;
                        return (
                          <TextField
                            fullWidth
                            label="Fahrzeug"
                            value={vehicleInfo ? `${vehicleInfo.model || 'Unbekannt'} (${vehicleInfo.licensePlate || 'Kein Kennzeichen'})` : 'Kein Fahrzeug zugewiesen'}
                            disabled
                            error={!vehicleInfo}
                            helperText={vehicleInfo ? 'Automatisch vom Fahrer übernommen' : 'Diesem Fahrer ist kein Fahrzeug zugewiesen.'}
                            InputProps={{
                              startAdornment: <CarIcon color={vehicleInfo ? "action" : "error"} sx={{ mr: 1 }} />
                            }}
                          />
                        );
                      })()
                    )}
                  </Grid>
                )}
              </Grid>
            </FormPaper>
          </>
        ) : (
          // Create/Edit Form
          <>
            {location.state?.duplicateRide && !isEditMode && (
              <Alert severity="info" sx={{ mb: 3 }}>
                {['completed', 'cancelled', 'cancelled_by_driver'].includes(location.state.duplicateRide.status)
                  ? 'Fahrt wird aus einer vorherigen Fahrt erstellt. Bitte überprüfen Sie die vorausgefüllten Daten.'
                  : 'Zusätzliches Taxi zu einer laufenden Fahrt wird angelegt (Sofortbestellung). Bitte überprüfen Sie die vorausgefüllten Daten und weisen Sie anschließend einen Fahrer zu.'}
              </Alert>
            )}
            <FormPaper elevation={2}>
              <Typography variant="h6" gutterBottom>
                Kundeninformation
              </Typography>
              
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 5 }}>
                  <Autocomplete
                    id="customer-select"
                    options={customerOptions}
                    sx={{ width: '100%' }}
                    getOptionLabel={(option) => {
                      // Wenn ein String übergeben wird (direktes Tippen)
                      if (typeof option === 'string') {
                        return option;
                      }
                      // Wenn eine "Neuen Kunden erstellen" Option ist
                      if (option.inputValue) {
                        return option.inputValue;
                      }
                      // Normale Kundenoption
                      return option.name || option.user?.name || '';
                    }}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    freeSolo
                    value={createNewCustomer ? { inputValue: formData.customer_name } : customerOptions.find(c => c.id === formData.customer_id) || null}
                    onChange={handleCustomerChange}
                    filterOptions={(options, params) => {
                      const { inputValue } = params;

                      // Filter by name or phone number
                      const filtered = inputValue
                        ? options.filter((option) => {
                            const name = (option.name || option.user?.name || '').toLowerCase();
                            const phone = (option.phoneNumber || option.user?.phoneNumber || '').toLowerCase();
                            const term = inputValue.toLowerCase();
                            return name.includes(term) || phone.includes(term);
                          })
                        : options;

                      // Suggest the creation of a new value
                      const isExisting = options.some((option) => inputValue === option.name || inputValue === option.user?.name);

                      if (inputValue !== '' && !isExisting) {
                        filtered.push({
                          inputValue,
                          name: `"${inputValue}" als neuen Telefonkunden anlegen`
                        });
                      }

                      return filtered;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Kunde (Name oder Telefonnummer)"
                        placeholder="Leer lassen für Laufkunde (Telefonkunde ohne Profil)"
                        error={!!formErrors.customer_id}
                        helperText={formErrors.customer_id}
                        sx={{ minWidth: { xs: 0, sm: '350px' }, width: '100%' }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <PersonIcon color="primary" sx={{ mr: 1 }} />
                              {params.InputProps.startAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      // Wenn es die "Neuen Kunden erstellen" Option ist
                      if (option.inputValue) {
                        return (
                          <li {...props}>
                            <Typography variant="body1" color="primary">
                              {option.name} (als Telefonkunde)
                            </Typography>
                          </li>
                        );
                      }
                      
                      // Normale Kundenoption
                      return (
                        <li {...props}>
                          <Box>
                            <Typography variant="body1">{option.name || option.user?.name}</Typography>
                            <Typography variant="body2" color="textSecondary">
                              {option.email || option.user?.email} | {option.phone || option.user?.phoneNumber}
                            </Typography>
                          </Box>
                        </li>
                      );
                    }}
                  />
                </Grid>
                
                {!createNewCustomer && (
                  <Grid size={{ xs: 12, md: 2 }} sx={{ 
                    display: { xs: 'none', md: 'flex' }, 
                    justifyContent: 'center', 
                    alignItems: 'center',
                  }}>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: 'text.secondary',
                      }}>
                      ODER
                    </Typography>
                  </Grid>
                )}
                
                {!createNewCustomer && (
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Autocomplete
                        id="template-select"
                        options={templates ? [...templates].sort((a, b) => {
                        // Sortierung priorisiert zuerst nach Kundennamen (falls vorhanden)
                        if (a.customer_name && b.customer_name) {
                          return a.customer_name.localeCompare(b.customer_name);
                        } else if (a.customer_name) {
                          return -1; // Vorlagen mit Kundennamen zuerst
                        } else if (b.customer_name) {
                          return 1;
                        }
                        // Dann nach Startadresse
                        return a.pickup_address.localeCompare(b.pickup_address);
                      }) : []}
                      getOptionLabel={(option) => {
                        if (option.customer_name) {
                          return `${option.customer_name}: ${option.pickup_address} → ${option.destination_address}`;
                        }
                        return `${option.pickup_address} → ${option.destination_address}`;
                      }}
                      filterOptions={(options, state) => {
                        // Erweiterte Filterung, um sowohl nach Kundennamen als auch nach Adressen zu filtern
                        const { inputValue } = state;
                        return options.filter(option => {
                          const customerMatch = option.customer_name && 
                            option.customer_name.toLowerCase().includes(inputValue.toLowerCase());
                          const pickupMatch = option.pickup_address.toLowerCase().includes(inputValue.toLowerCase());
                          const destMatch = option.destination_address.toLowerCase().includes(inputValue.toLowerCase());
                          return customerMatch || pickupMatch || destMatch;
                        });
                      }}
                      onChange={handleTemplateSelect}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Aus Vorlage erstellen"
                          sx={{ 
                            width: '100%',
                            minWidth: { xs: 0, sm: '350px' }
                          }}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <TemplateIcon color="primary" sx={{ mr: 1 }} />
                                {params.InputProps.startAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      ListboxProps={{
                        style: { 
                          maxWidth: 'none',
                          minWidth: 'min(450px, calc(100vw - 32px))'
                        }
                      }}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box sx={{ width: '100%', py: 0.5 }}>
                            <Typography 
                              sx={{ 
                                fontSize: '0.875rem', 
                                fontWeight: option.customer_name ? 'bold' : 'normal',
                                lineHeight: 1.3
                              }}
                            >
                              {option.customer_name ? (
                                <span style={{ color: 'primary.main' }}>{option.customer_name}:</span>
                              ) : ''}
                              {' '}{option.pickup_address} → {option.destination_address}
                            </Typography>
                            <Typography 
                              sx={{ 
                                fontSize: '0.75rem', 
                                color: 'text.secondary',
                                lineHeight: 1.2,
                                mt: 0.25
                              }}
                            >
                              {!option.customer_name ? 'Kunde: Nicht angegeben' : ''} 
                              {option.customer_name ? '' : ' | '}
                              Typ: {getVehicleTypeDescription(parseInt(option.vehicle_type))}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </Grid>
                )}
                
                {createNewCustomer && (
                  <>
                    <Grid size={{ xs: 12, md: 7 }} sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                      <TextField
                        fullWidth
                        id="customer_phone"
                        name="customer_phone"
                        label="Telefonnummer (optional)"
                        value={formData.customer_phone}
                        onChange={handleCustomerPhoneChange}
                        error={!!formErrors.customer_phone}
                        helperText={formErrors.customer_phone || "Kann später in der Kundenverwaltung ergänzt werden"}
                        placeholder="+49123456789"
                        sx={{ maxWidth: { xs: 'none', md: '400px' } }}
                      />
                    </Grid>
                    <Grid size={12}>
                      <Alert severity="info">
                        Ein neuer Telefonkunde wird erstellt. Die Kundeninformationen können später in der Kundenverwaltung bearbeitet werden.
                      </Alert>
                    </Grid>
                  </>
                )}
              </Grid>
            </FormPaper>
            
            <OrderTypeBox>
              <Typography variant="h6" gutterBottom>
                Bestelltyp
              </Typography>
              
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: formData.order_type === '2' ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        p: { xs: 1.5, sm: 2 },
                        cursor: 'pointer',
                        bgcolor: formData.order_type === '2' ? 'primary.light' : 'background.paper',
                        '&:hover': {
                          bgcolor: formData.order_type === '2' ? 'primary.light' : 'action.hover'
                        },
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => handleOrderTypeChange({ target: { value: '2' } })}
                    >
                      <FormControlLabel 
                        value="2" 
                        control={<Radio 
                          checked={formData.order_type === '2'} 
                          sx={{
                            color: '#000000',
                            '&.Mui-checked': {
                              color: '#000000'
                            }
                          }}
                        />} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <ImmediateIcon sx={{ mr: 1 }} />
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Sofortbestellung</Typography>
                              <Typography variant="body2" color="text.secondary">Fahrt beginnt sofort nach Fahrerzuweisung</Typography>
                            </Box>
                          </Box>
                        } 
                        sx={{ margin: 0, width: '100%' }}
                      />
                    </Box>
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: formData.order_type === '1' ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        p: { xs: 1.5, sm: 2 },
                        cursor: 'pointer',
                        bgcolor: formData.order_type === '1' ? 'primary.light' : 'background.paper',
                        '&:hover': {
                          bgcolor: formData.order_type === '1' ? 'primary.light' : 'action.hover'
                        },
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => handleOrderTypeChange({ target: { value: '1' } })}
                    >
                      <FormControlLabel 
                        value="1" 
                        control={<Radio 
                          checked={formData.order_type === '1'} 
                          sx={{
                            color: '#000000',
                            '&.Mui-checked': {
                              color: '#000000'
                            }
                          }}
                        />} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <ScheduleIcon sx={{ mr: 1 }} />
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Vorbestellung</Typography>
                              <Typography variant="body2" color="text.secondary">Fahrt für einen späteren Zeitpunkt planen</Typography>
                            </Box>
                          </Box>
                        } 
                        sx={{ margin: 0, width: '100%' }}
                      />
                    </Box>
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: formData.order_type === '3' ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        p: { xs: 1.5, sm: 2 },
                        cursor: 'pointer',
                        bgcolor: formData.order_type === '3' ? 'primary.light' : 'background.paper',
                        '&:hover': {
                          bgcolor: formData.order_type === '3' ? 'primary.light' : 'action.hover'
                        },
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => handleOrderTypeChange({ target: { value: '3' } })}
                    >
                      <FormControlLabel 
                        value="3" 
                        control={<Radio 
                          checked={formData.order_type === '3'} 
                          sx={{
                            color: '#000000',
                            '&.Mui-checked': {
                              color: '#000000'
                            }
                          }}
                        />} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <RecurringIcon sx={{ mr: 1 }} />
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Dauerauftrag</Typography>
                              <Typography variant="body2" color="text.secondary">Wiederkehrende Fahrten (z.B. Kranken-/Dialysefahrten)</Typography>
                            </Box>
                          </Box>
                        } 
                        sx={{ margin: 0, width: '100%' }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </FormControl>
              
              {formData.order_type === '1' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Wann soll die Fahrt stattfinden?
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                        <DateTimePicker
                          label="Datum und Uhrzeit"
                          value={formData.scheduled_time}
                          onChange={handleScheduledTimeChange}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              variant: "outlined",
                              error: !!formErrors.scheduled_time,
                              helperText: formErrors.scheduled_time || "Wählen Sie Datum und Uhrzeit für die Vorbestellung",
                              InputProps: {
                                style: { backgroundColor: 'white' }
                              }
                            }
                          }}
                          minDate={new Date()}
                          ampm={false}
                          format="dd.MM.yyyy HH:mm"
                          minutesStep={5}
                          views={['year', 'month', 'day', 'hours', 'minutes']}
                          // Kalender mit größeren Flächen für mobile Bedienung
                          componentsProps={{
                            actionBar: {
                              actions: ['clear', 'today', 'accept'],
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    {formData.scheduled_time && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Geplanter Zeitpunkt
                          </Typography>
                          <Typography>
                            {new Date(formData.scheduled_time).toLocaleString('de-DE', {
                              weekday: 'long',
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
              
              {formData.order_type === '3' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Dauerauftrag konfigurieren
                  </Typography>
                  
                  <Grid container spacing={3}>
                    {/* Zeitraum */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                        <DatePicker
                          label="Startdatum"
                          value={formData.recurring_start_date}
                          onChange={handleRecurringStartDateChange}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              variant: "outlined",
                              error: !!formErrors.recurring_start_date,
                              helperText: formErrors.recurring_start_date || "Ab wann soll der Dauerauftrag beginnen?",
                              InputProps: {
                                style: { backgroundColor: 'white' }
                              }
                            }
                          }}
                          minDate={new Date()}
                          format="dd.MM.yyyy"
                        />
                      </LocalizationProvider>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                        <DatePicker
                          label="Enddatum"
                          value={formData.recurring_end_date}
                          onChange={handleRecurringEndDateChange}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              variant: "outlined",
                              error: !!formErrors.recurring_end_date,
                              helperText: formErrors.recurring_end_date || "Bis wann soll der Dauerauftrag laufen?",
                              InputProps: {
                                style: { backgroundColor: 'white' }
                              }
                            }
                          }}
                          minDate={formData.recurring_start_date || new Date()}
                          format="dd.MM.yyyy"
                        />
                      </LocalizationProvider>
                    </Grid>
                    
                    {/* Uhrzeit */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                        <TimePicker
                          label="Abholzeit"
                          value={formData.recurring_time}
                          onChange={handleRecurringTimeChange}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              variant: "outlined",
                              error: !!formErrors.recurring_time,
                              helperText: formErrors.recurring_time || "Um welche Uhrzeit soll abgeholt werden?",
                              InputProps: {
                                style: { backgroundColor: 'white' }
                              }
                            }
                          }}
                          ampm={false}
                          format="HH:mm"
                          minutesStep={5}
                        />
                      </LocalizationProvider>
                    </Grid>
                    
                    {/* Wochentage */}
                    <Grid size={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Wochentage auswählen *
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 1,
                        maxWidth: { xs: '100%', md: '50%' }
                      }}>
                        {[
                          { index: 1, label: 'Mo' },
                          { index: 2, label: 'Di' },
                          { index: 3, label: 'Mi' },
                          { index: 4, label: 'Do' },
                          { index: 5, label: 'Fr' },
                          { index: 6, label: 'Sa' },
                          { index: 0, label: 'So' }
                        ].map((day) => (
                          <FormControlLabel
                            key={day.index}
                            control={
                              <Checkbox
                                checked={formData.recurring_weekdays.includes(day.index)}
                                onChange={() => handleWeekdayChange(day.index)}
                                color="primary"
                                sx={{
                                  color: '#000000', // Schwarze Checkbox
                                  '&.Mui-checked': {
                                    color: '#000000' // Schwarze Markierung wenn ausgewählt
                                  },
                                  padding: '4px' // Reduziertes Padding um Checkbox
                                }}
                              />
                            }
                            label={
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 'bold', 
                                  textAlign: 'center',
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' } // Kleinere Schrift auf Mobile
                                }}
                              >
                                {day.label}
                              </Typography>
                            }
                            sx={{ 
                              margin: 0,
                              border: formData.recurring_weekdays.includes(day.index) 
                                ? '2px solid' 
                                : '1px solid',
                              borderColor: formData.recurring_weekdays.includes(day.index) 
                                ? 'primary.main' 
                                : 'divider',
                              borderRadius: 1,
                              p: { xs: 1, sm: 1.5 }, // Weniger Padding auf Mobile
                              flex: '1 1 calc((100% - 6px) / 7)', // 7 Container mit Gaps
                              minWidth: { xs: 40, sm: 60 }, // Noch kleinere Container auf Mobile
                              maxWidth: { xs: 50, sm: 100 },
                              minHeight: 48,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              bgcolor: formData.recurring_weekdays.includes(day.index) 
                                ? 'primary.light' 
                                : 'background.paper',
                              '&:hover': {
                                bgcolor: formData.recurring_weekdays.includes(day.index) 
                                  ? 'primary.light' 
                                  : 'action.hover'
                              },
                              '& .MuiFormControlLabel-label': {
                                width: '100%',
                                textAlign: 'center',
                                marginLeft: '4px' // Reduzierter Abstand zwischen Checkbox und Label
                              }
                            }}
                          />
                        ))}
                      </Box>
                      {formErrors.recurring_weekdays && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          {formErrors.recurring_weekdays}
                        </FormHelperText>
                      )}
                    </Grid>
                    
                    {/* Vorschau der geplanten Termine */}
                    {formData.recurring_start_date && formData.recurring_end_date && 
                     formData.recurring_time && formData.recurring_weekdays.length > 0 && (
                      <Grid size={12}>
                        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Vorschau der geplanten Termine
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Die erste Fahrt wird erstellt für:{' '}
                            {(() => {
                              const weekdayNames = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
                              return formData.recurring_weekdays.map(day => weekdayNames[day]).join(', ');
                            })()}
                            {' '}um {formData.recurring_time ? new Date(formData.recurring_time).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : ''}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Zeitraum:</strong> {formData.recurring_start_date ? new Date(formData.recurring_start_date).toLocaleDateString('de-DE') : ''} bis{' '}
                            {formData.recurring_end_date ? new Date(formData.recurring_end_date).toLocaleDateString('de-DE') : ''}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </OrderTypeBox>
            
            <FormPaper elevation={2} sx={{ position: 'relative', overflow: 'visible' }}>
              <Typography variant="h6" gutterBottom>
                Fahrtinformationen
              </Typography>
              
              <Grid container spacing={3}>
                <Grid size={12}>
                  <PlaceAutocomplete
                    required
                    fullWidth
                    label="Abholadresse"
                    value={formData.pickup_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, pickup_address: e.target.value }))}
                    onPlaceSelect={(address, location) => {
                      setFormData(prev => ({
                        ...prev,
                        pickup_address: address,
                        pickup_location: location
                      }));
                    }}
                    error={!!formErrors.pickup_address}
                    helperText={formErrors.pickup_address}
                    placeholder="Straße, Hausnummer, PLZ, Ort"
                  />
                </Grid>

                <Grid size={12} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' }, paddingTop: '0 !important' }}>
                  <Tooltip title="Start- und Zieladresse tauschen">
                    <span>
                      <IconButton
                        onClick={handleSwapAddresses}
                        disabled={!formData.pickup_address && !formData.destination_address}
                        size="small"
                        color="primary"
                        sx={{
                          width: 44,
                          height: 44,
                          border: '1px solid',
                          borderColor: 'divider',
                          backgroundColor: 'background.paper',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <SwapIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>

                <Grid size={12} sx={{ paddingTop: '0 !important' }}>
                  <PlaceAutocomplete
                    fullWidth
                    label="Zieladresse"
                    value={formData.destination_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, destination_address: e.target.value }))}
                    onPlaceSelect={(address, location) => {
                      setFormData(prev => ({
                        ...prev,
                        destination_address: address,
                        destination_location: location
                      }));
                    }}
                    error={!!formErrors.destination_address}
                    helperText={formErrors.destination_address}
                    placeholder="Straße, Hausnummer, PLZ, Ort"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="vehicle-type-label">Fahrzeugtyp</InputLabel>
                    <Select
                      labelId="vehicle-type-label"
                      id="vehicle_type"
                      name="vehicle_type"
                      value={formData.vehicle_type}
                      onChange={handleChange}
                      label="Fahrzeugtyp"
                    >
                      {VEHICLE_TYPE_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={String(opt.value)}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {CAPABILITIES_PER_TYPE[Number(formData.vehicle_type)]?.length > 0 && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel id="special-capabilities-label">Spezialfähigkeiten</InputLabel>
                      <Select
                        labelId="special-capabilities-label"
                        id="special_capabilities"
                        multiple
                        value={formData.special_capabilities}
                        onChange={(e) => setFormData({ ...formData, special_capabilities: e.target.value })}
                        label="Spezialfähigkeiten"
                        renderValue={(selected) => selected.map(s => SPECIAL_CAPABILITIES[s] || s).join(', ')}
                      >
                        {CAPABILITIES_PER_TYPE[Number(formData.vehicle_type)].map(cap => (
                          <MenuItem key={cap} value={cap}>{SPECIAL_CAPABILITIES[cap] || cap}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 0.5, sm: 2 }, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={formData.is_krankenfahrt}
                          onChange={(e) => setFormData({
                            ...formData,
                            is_krankenfahrt: e.target.checked,
                            medical_transport_order_id: e.target.checked ? formData.medical_transport_order_id : ''
                          })}
                        />
                      }
                      label="Krankenfahrt"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={formData.is_rechnungsfahrt}
                          onChange={(e) => setFormData({ ...formData, is_rechnungsfahrt: e.target.checked })}
                        />
                      }
                      label="Rechnungsfahrt"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={formData.is_festpreis}
                          onChange={(e) => setFormData({ ...formData, is_festpreis: e.target.checked, festpreis_eur: e.target.checked ? formData.festpreis_eur : '' })}
                        />
                      }
                      label="Festpreis"
                    />
                    {formData.is_festpreis && (
                      <TextField
                        type="number"
                        label="Festpreis-Betrag"
                        value={formData.festpreis_eur}
                        onChange={(e) => setFormData({ ...formData, festpreis_eur: e.target.value })}
                        placeholder="z.B. 25.00"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">€</InputAdornment>,
                          inputProps: { min: 0, step: 0.5 }
                        }}
                        helperText="Betrag in Euro eingeben (z.B. 25.00)"
                        sx={{ minWidth: { xs: 0, sm: 200 }, width: { xs: '100%', sm: 'auto' } }}
                      />
                    )}
                  </Box>
                </Grid>

                {formData.is_krankenfahrt && (
                  <Grid size={12}>
                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <MedicalIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight="medium">
                          Verordnung zuordnen
                        </Typography>
                      </Box>

                      <Autocomplete
                        fullWidth
                        options={medicalTransportOrders}
                        loading={medicalTransportOrdersLoading}
                        value={selectedMedicalTransportOrder}
                        onChange={handleMedicalTransportOrderChange}
                        getOptionLabel={(option) => {
                          if (!option) return '';
                          const remaining = Number(option.maxRides || 0) > 0
                            ? `, Rest: ${Math.max(Number(option.maxRides || 0) - Number(option.usedRides || 0) - Number(option.plannedRides || 0), 0)}`
                            : '';
                          return `${option.patientName || 'Ohne Patient'} - ${option.healthInsuranceName || 'Ohne Krankenkasse'} (${formatDate(option.validFrom)} bis ${formatDate(option.validUntil)}${remaining})`;
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Verordnungsfall"
                            helperText={formErrors.medical_transport_order_id || 'Optional, aber empfohlen für Verordnungsfahrten und spätere Abrechnung.'}
                            error={!!formErrors.medical_transport_order_id}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {medicalTransportOrdersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                        renderOption={(props, option) => {
                          const approval = getOption(APPROVAL_STATUS_OPTIONS, option.approvalStatus);
                          const maxRides = Number(option.maxRides) || 0;
                          const remaining = maxRides > 0
                            ? Math.max(maxRides - Number(option.usedRides || 0) - Number(option.plannedRides || 0), 0)
                            : null;

                          return (
                            <Box component="li" {...props} sx={{ display: 'block !important' }}>
                              <Typography variant="body2" fontWeight="medium">
                                {option.patientName || 'Ohne Patient'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {option.healthInsuranceName || 'Ohne Krankenkasse'} · {formatDate(option.validFrom)} bis {formatDate(option.validUntil)}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                <Chip size="small" label={approval.label} color={approval.color} />
                                <Chip
                                  size="small"
                                  label={remaining === null ? 'Kein Kontingent' : `${remaining} Restfahrten`}
                                  variant="outlined"
                                />
                              </Box>
                            </Box>
                          );
                        }}
                      />

                      {selectedMedicalTransportOrder && (
                        <Alert severity={medicalTransportOrderCheck.severity} sx={{ mt: 2 }}>
                          {medicalTransportOrderCheck.messages.map((message) => (
                            <Typography key={message} variant="body2">
                              {message}
                            </Typography>
                          ))}
                        </Alert>
                      )}
                    </Box>
                  </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    id="notes"
                    name="notes"
                    label="Notizen für den Fahrer"
                    placeholder="z.B. Kunde wartet am Hintereingang, Rollstuhlfahrer, ..."
                    multiline
                    minRows={2}
                    maxRows={4}
                    value={formData.notes}
                    onChange={handleChange}
                  />
                </Grid>

                {!isEditMode && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={saveAsTemplate}
                          onChange={(e) => setSaveAsTemplate(e.target.checked)}
                          icon={<BookmarkIcon />}
                          checkedIcon={<BookmarkIcon />}
                        />
                      }
                      label={
                        <Tooltip title="Die Fahrt wird als Vorlage gespeichert, die später wiederverwendet werden kann">
                          <Typography variant="body2">Als Vorlage speichern</Typography>
                        </Tooltip>
                      }
                    />
                  </Grid>
                )}

              </Grid>
            </FormPaper>

            {/* Direktzuweisung — nur beim Erstellen, nicht beim Bearbeiten */}
            {isEditMode ? (
              <FormPaper elevation={2} sx={{ bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom color="text.secondary">
                  Fahrerzuweisung
                </Typography>
                <Alert severity="info">
                  Die Fahrerzuweisung kann nicht im Bearbeitungsmodus geändert werden. Bitte nutzen Sie dafür die Funktion „Fahrer zuweisen" in der Fahrtdetailansicht.
                </Alert>
                {currentRide?.driverName && (
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Aktuell zugewiesen: <strong>{currentRide.driverName}</strong>
                  </Typography>
                )}
              </FormPaper>
            ) : (
              <FormPaper elevation={2}>
                <Typography variant="h6" gutterBottom>
                  Fahrt direkt zuweisen
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Optional: Wählen Sie einen Fahrer, um die Fahrt direkt zuzuweisen.
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Autocomplete
                      id="driver_id"
                      options={(formData.order_type === '1' || formData.order_type === '3') ? allDrivers : availableDrivers}
                      getOptionLabel={(option) => option.name || option.user?.name || `Fahrer ${option.id}`}
                      value={((formData.order_type === '1' || formData.order_type === '3') ? allDrivers : availableDrivers).find(d => d.id === formData.driver_id) || null}
                      onChange={handleDriverChange}
                      renderInput={(params) =>
                        <TextField
                          {...params}
                          label="Fahrer auswählen"
                          fullWidth
                          error={!!formErrors.driver_id}
                          helperText={formErrors.driver_id || (
                            (formData.order_type === '1' || formData.order_type === '3')
                              ? "Für Vor-/Dauerbestellungen können alle Fahrer zugewiesen werden."
                              : "Für Sofortbestellungen können nur Online-Fahrer mit zugewiesenem Fahrzeug zugewiesen werden."
                          )}
                        />
                      }
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box>
                            <Typography variant="body1">{option.name || option.user?.name || `Fahrer ${option.id}`}</Typography>
                            <Typography variant="body2" color="textSecondary">
                              {option.isOnline ? "Online" : "Offline"} | Rating: {option.rating?.toFixed(1) || "N/A"} | Tel: {option.phone || option.user?.phone || "N/A"}
                            </Typography>
                            {formData.order_type !== '1' && formData.order_type !== '3' && option.vehicle && (
                              <Typography variant="body2" color="textSecondary">
                                Fahrzeug: {option.vehicle.model || 'Unbekannt'} {option.vehicle.licensePlate ? `(${option.vehicle.licensePlate})` : ''}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )}
                    />
                  </Grid>
                </Grid>

                {formData.driver_id && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {(formData.order_type === '1' || formData.order_type === '3')
                      ? "Fahrzeugdaten werden erst bei Fahrtantritt durch die Mobile-App gesetzt."
                      : "Bei Zuweisung eines Fahrers für eine Sofortbestellung wird der Status auf 'Akzeptiert' gesetzt."}
                  </Alert>
                )}
              </FormPaper>
            )}
          </>
        )}
        
        {/* Dialog: Fahrt ohne Zieladresse erstellen */}
        <Dialog open={showNoDestinationDialog} onClose={() => setShowNoDestinationDialog(false)}>
          <DialogTitle>Kein Ziel angegeben</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mt: 1 }}>
              Es wurde keine Zieladresse eingegeben. Möchten Sie die Fahrt trotzdem ohne Ziel erstellen?
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowNoDestinationDialog(false)}>
              Ziel eingeben
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => {
                setShowNoDestinationDialog(false);
                handleSubmit(null, true);
              }}
            >
              Trotzdem erstellen
            </Button>
          </DialogActions>
        </Dialog>

        {/* Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: { xs: 1, sm: 2 },
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            position: { xs: 'fixed', sm: 'static' },
            left: { xs: 0, sm: 'auto' },
            right: { xs: 0, sm: 'auto' },
            bottom: { xs: 0, sm: 'auto' },
            zIndex: { xs: 20, sm: 'auto' },
            p: { xs: 1.5, sm: 0 },
            bgcolor: { xs: 'background.paper', sm: 'transparent' },
            borderTop: { xs: '1px solid', sm: 'none' },
            borderColor: { xs: 'divider', sm: 'transparent' },
            boxShadow: { xs: 3, sm: 'none' },
          }}
        >
          <Button 
            variant="outlined" 
            onClick={handleCancel}
            startIcon={<CancelIcon />}
            fullWidth={isMobile}
          >
            Abbrechen
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            startIcon={isCalculatingRoute ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={isLoading || isCalculatingRoute}
            fullWidth={isMobile}
          >
            {isCalculatingRoute ? 'Fahrtdauer wird berechnet...' :
             isAssignMode ? 'Fahrer zuweisen' : 
             isEditMode ? 'Änderungen speichern' : 
             'Fahrt erstellen'}
          </Button>
        </Box>
      </form>
      </Box>
    </APIProvider>
  );
};

export default RideForm;
